import React, { ChangeEventHandler, useCallback, useState, useMemo, MouseEventHandler } from 'react'
import AvatarCircle from 'components/Common/AvatarCircle/AvatarCircle'
import ListItem from 'components/Luxkit/List/ListItem'
import Group from 'components/utils/Group'
import { connect } from 'react-redux'
import CaptionTextBlock from 'components/Luxkit/TextBlocks/CaptionTextBlock'
import TextInputOnly from 'components/Common/Form/Input/TextInputOnly'
import LineSearchIcon from 'components/Luxkit/Icons/line/LineSearchIcon'
import { useAppDispatch } from 'hooks/reduxHooks'
import useBusinessTravellerEmployeeQuery from 'businessTraveller/hooks/useEmployeeQuery'
import { sortBy } from 'lib/arrayUtils'
import setSelectedBusinessTravellerEmployeeIds from 'businessTraveller/actions/setSelectedBusinessTravellerEmployeeIds'
import Divider from 'components/Luxkit/Divider'
import { showSnackbar } from 'components/Luxkit/Snackbar/AppSnackbar'

interface EmployeeOption {
  id: string
  firstName: string
  lastName: string
  isSelected: boolean
}

interface MappedStateProps {
  businessAccount?: BusinessTraveller.CurrentBusinessAccount
  selectedTravellerEmployeeIds?: string[]
}

interface Props {
  onTravellerSelect: (selectedTravellerEmployeeIds: string[]) => void
}

function BusinessTravellerSelectContent(props: Props & MappedStateProps) {
  const { businessAccount, selectedTravellerEmployeeIds, onTravellerSelect } = props
  const { employees } = useBusinessTravellerEmployeeQuery()

  const employeeOptions = useMemo<EmployeeOption[]>(() => {
    if (!employees) {
      return []
    }

    const options: EmployeeOption[] = Object.values(employees).map<EmployeeOption>((employee) => {
      return {
        id: employee.id,
        firstName: employee.firstName,
        lastName: employee.lastName,
        isSelected: !!selectedTravellerEmployeeIds?.find((selectedEmployee) => selectedEmployee === employee.id),
      }
    })

    return sortBy(options, (employee) => employee.firstName + employee.lastName, 'asc')
  }, [employees, selectedTravellerEmployeeIds])

  const dispatch = useAppDispatch()

  const [filteredEmployeeOptions, setFilteredEmployeeOptions] = useState<EmployeeOption[]>([])

  const handleOnChange = useCallback<ChangeEventHandler<HTMLInputElement>>((e) => {
    if (employeeOptions.length) {
      if (!e.currentTarget.value) {
        setFilteredEmployeeOptions([])
      } else {
        setFilteredEmployeeOptions(employeeOptions.filter((employee) => {
          return employee.firstName.toLocaleLowerCase().includes(e.currentTarget.value) ||
          employee.lastName.toLocaleLowerCase().includes(e.currentTarget.value.toLocaleLowerCase())
        }))
      }
    }
  }, [employeeOptions])

  const handleEmployeeClick = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    const employeeId = event.currentTarget.dataset.employeeid
    const selectedEmployee = employeeOptions.find((employee) => employee.id === employeeId)

    if (selectedEmployee) {
      const selected = [selectedEmployee.id]
      dispatch(setSelectedBusinessTravellerEmployeeIds(selected))
      showSnackbar('Traveller successfully updated.', 'success')
      onTravellerSelect(selected)
    }
  }, [dispatch, employeeOptions, onTravellerSelect])

  const isBusinessAccountSelected = useMemo<boolean>(() => {
    return !!selectedTravellerEmployeeIds?.find(id => id === businessAccount?.employee?.id)
  }, [businessAccount?.employee?.id, selectedTravellerEmployeeIds])

  const showFilteredEmployees = filteredEmployeeOptions.length > 0

  return (
    <Group direction="vertical" gap={12}>
      <TextInputOnly
        type="text"
        placeholder="Search traveller name"
        onChange={handleOnChange}
        endIcon={<LineSearchIcon />}
        noValidationSpacing
      />
      {!showFilteredEmployees && (
        <>
          {!!businessAccount?.employee?.id &&
            <ListItem
              startIcon={
                <AvatarCircle
                  firstName={businessAccount?.employee?.firstName}
                  lastName={businessAccount?.employee?.lastName}
                  nonInteractable
                />}
              title={`${businessAccount?.employee?.firstName} ${businessAccount?.employee?.lastName}`}
              data-employeeid={businessAccount.employee.id}
              selected={isBusinessAccountSelected}
              onClick={handleEmployeeClick}
            />
          }
          <Divider kind="primary" />
          <CaptionTextBlock variant="large">
            {businessAccount?.business?.name}'s team members {employeeOptions?.length}
          </CaptionTextBlock>
          <div>
            {employeeOptions?.map((employeeOption) => <ListItem
              key={employeeOption.id}
              startIcon={
                <AvatarCircle
                  firstName={employeeOption.firstName}
                  lastName={employeeOption.lastName}
                  nonInteractable
                />}
              title={`${employeeOption.firstName} ${employeeOption.lastName}`}
              data-employeeid={employeeOption.id}
              selected={employeeOption.isSelected}
              onClick={handleEmployeeClick}
            />)}
          </div>
        </>
      )}
      {showFilteredEmployees && (
        <div>
          {filteredEmployeeOptions.map((employeeOption) => <ListItem
            key={employeeOption.id}
            startIcon={<AvatarCircle
              firstName={employeeOption.firstName}
              lastName={employeeOption.lastName}
              nonInteractable
            />}
            title={`${employeeOption.firstName} ${employeeOption.lastName}`}
            data-employeeid={employeeOption.id}
            selected={employeeOption.isSelected}
            onClick={handleEmployeeClick}
          />)}
        </div>
      )}
    </Group>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => {
  return {
    businessAccount: state.businessTraveller.currentBusinessAccount,
    selectedTravellerEmployeeIds: state.businessTraveller.selectedTravellerEmployeeIds,
  }
})(BusinessTravellerSelectContent)
